import { endOfDay } from "date-fns";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { Activity, Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import CoachingFile from "@app/scene/dashboard/types/CoachingFile";
import CoachingNote from "@app/scene/dashboard/types/CoachingNote";
import { notifyActivityRelatedEntities } from "@app/store/activity/actions";
import { ActivityPayload } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganization, getOrganizationId } from "@app/store/iam";
import { getAllUsersOfEntireOrganization } from "@app/store/members";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import { activityLayoutModel } from "@app/util/layout/impl";
import { parseApiDate } from "@app/util/parsers";

import { uploadCoachingFiles } from "../files/actions";

import {
  CoachingTasksSort,
  createCoachingTask,
  CreateCoachingTaskRequest,
  createCoachingTasksForTeam,
  CreateCoachingTasksForTeamRequest,
  deleteCoachingTask,
  fetchCoachingTasks,
  fetchLinkedTasks,
  fetchSuggestedActionOwner,
  injectCoachingTask,
  openCoachingTaskEdit,
  setCoachingTaskMentions,
  toggleCoachingTaskComplete,
  updateCoachingTask,
} from "./actions";
import { getCoachingTasks, getLinkedCoachingTasksById } from "./selectors";

const ORDER_BY_SORT: Record<CoachingTasksSort, string> = {
  "dueDate-asc": "startAt",
  "dueDate-desc": "-startAt",
  "updatedAt-asc": "updatedAt",
  "updatedAt-desc": "-updatedAt",
};

function* onFetchCoachingTasks({ payload }: ReturnType<typeof fetchCoachingTasks.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const $filters: Record<string, unknown> = {
      assigneeId: { $eq: payload.tabUserId },
      crmActivityTypeId: { $eq: payload.crmActivityTypeId },
      includeAccessStatus: true,
      includeCoachingTasks: true,
      // Without this, the activities list response omits the `files` relation,
      // so a task with attachments loses its chip on any refresh / refetch even
      // though crm_activities.files is correctly persisted on the BE.
      includeFiles: true,
    };
    if (payload.filter === "active") {
      $filters.completed = { $eq: false };
    } else if (payload.filter === "completed") {
      $filters.completed = { $eq: true };
    }

    const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters,
      $limit: payload.limit,
      $offset: payload.offset,
      $order: ORDER_BY_SORT[payload.sortBy],
    });

    yield put(
      fetchCoachingTasks.success({
        append: !!payload.append,
        silent: !!payload.silent,
        tasks: response.data,
        total: response.total,
      })
    );
  } catch (error) {
    // Silent poll failures stay invisible (no toast) — the next poll retries.
    if (!payload.silent) {
      yield put(fetchCoachingTasks.failure());
      yield put(handleError({ error }));
    }
  }
}

// Shared shape for a coaching-task activity — reused by the single create and
// the per-rep team-push builder so the payload can't drift between them.
const buildCoachingActivityPayload = (params: {
  assigneeId: User["id"];
  coachingNoteId?: CoachingNote["id"];
  crmActivityTypeId: ActivityType["id"];
  dueDate: null | string;
  fileIds?: Array<number>;
  layoutId?: number;
  name: string;
  note?: string;
  subjectUserId?: User["id"];
}): ActivityPayload => ({
  account: null,
  allDay: true,
  assigneeId: params.assigneeId,
  coachingNoteId: params.coachingNoteId ?? undefined,
  contact: null,
  crmActivityType: { id: params.crmActivityTypeId },
  deal: null,
  files: params.fileIds && params.fileIds.length > 0 ? params.fileIds : undefined,
  ...(params.layoutId !== undefined ? { layoutId: params.layoutId } : {}),
  name: params.name,
  note: params.note ?? undefined,
  startAt: params.dueDate ? endOfDay(parseApiDate(params.dueDate)).toISOString() : undefined,
  subjectUserId: params.subjectUserId,
});

// Exposed so the note-with-task orchestrator (TART-27025) can run the same
export function* runCreateCoachingTask(payload: CreateCoachingTaskRequest) {
  const organization: Organization = yield select(getOrganization);
  const defaultLayoutId = activityLayoutModel.defaultLayout.id;

  const activityPayload = buildCoachingActivityPayload({
    assigneeId: payload.assigneeId,
    coachingNoteId: payload.coachingNoteId,
    crmActivityTypeId: payload.crmActivityTypeId,
    dueDate: payload.dueDate,
    fileIds: payload.fileIds,
    name: payload.name,
    note: payload.note,
    subjectUserId: payload.subjectUserId,
  });

  let newActivity: Activity = yield callApi(
    "createActivity",
    organization.id,
    defaultLayoutId,
    activityPayload,
    // Coaching tasks only collect a few fields, so skip layout validation —
    // mandatory activity-layout fields must not block task creation.
    { ignoreLayout: true, includeFiles: true }
  );

  // The create response may omit the assignee relation; hydrate it from the
  // known assigneeId so the optimistic prepend's current-tab check is reliable.
  if (!newActivity.assignee) {
    const allUsers: User[] = yield select(getAllUsersOfEntireOrganization);
    const assignee = allUsers.find(({ id }) => id === payload.assigneeId);
    if (assignee) {
      newActivity = { ...newActivity, assignee };
    }
  }

  yield put(notifyAboutChanges({ added: [newActivity], entityType: EntityType.ACTIVITY }));
  yield put(notifyActivityRelatedEntities(newActivity));
  if (payload.mentions && payload.mentions.length > 0) {
    yield put(setCoachingTaskMentions({ mentions: payload.mentions, taskId: newActivity.id }));
  }
  return newActivity;
}

// Upload newFiles to the rep's pool and fold their resulting platform fileIds
// into the task payload. Returns a payload with newFiles cleared and fileIds
// merged — the orchestrator uses this separately from runCreateCoachingTask
// so a failed task POST can stash an already-uploaded-files payload for retry
// without re-uploading.
// Upload N files to the rep's pool, re-fetch any whose `fileId` came back
// nullish (the BE creates the coaching-file row before the underlying
// platform file row finishes — straight `.map(({ fileId }) => fileId)`
// otherwise yields undefineds that JSON-serialize to nulls and the activity
// PUT/POST silently drops them). Throws if any platform fileId still can't
// be resolved after the refetch — explicit failure beats silent data loss.
// Pool-state success dispatch happens AFTER the validation throw so a
// failure can't leave orphaned chips in the Files panel for a write that
// won't go through.
function* uploadCoachingFilesToPool(
  orgId: Organization["id"],
  subjectUserId: User["id"],
  files: Array<Blob | File>
) {
  const uploaded: CoachingFile[] = yield all(
    files.map((file) => callApi("createCoachingFile", orgId, file, subjectUserId))
  );
  const verified: CoachingFile[] = yield all(
    uploaded.map((cf) =>
      cf.fileId !== undefined && cf.fileId !== null
        ? cf
        : callApi("fetchCoachingFile", orgId, cf.id)
    )
  );
  const fileIds = verified
    .map(({ fileId }) => fileId)
    .filter((fileId): fileId is number => fileId !== undefined && fileId !== null);
  if (fileIds.length !== files.length) {
    throw new Error(
      `Coaching-file upload returned ${fileIds.length}/${files.length} resolved fileIds`
    );
  }
  return { fileIds, verified };
}

// Wrapper for the single-subject flows: also folds the uploaded files into the
// (single) Files-panel pool state. Team pushes call uploadCoachingFilesToPool
// directly so each rep's files don't leak into the manager's own open pool.
function* uploadAndResolveCoachingFiles(
  orgId: Organization["id"],
  subjectUserId: User["id"],
  files: Array<Blob | File>
) {
  const result: { fileIds: number[]; verified: CoachingFile[] } = yield call(
    uploadCoachingFilesToPool,
    orgId,
    subjectUserId,
    files
  );
  yield put(uploadCoachingFiles.success(result.verified));
  return result;
}

export function* uploadCoachingTaskFiles(payload: CreateCoachingTaskRequest) {
  if (!payload.newFiles || payload.newFiles.length === 0) {
    return payload;
  }
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const { fileIds: uploadedFileIds }: { fileIds: number[] } = yield call(
    uploadAndResolveCoachingFiles,
    orgId,
    payload.subjectUserId,
    payload.newFiles
  );
  const finalPayload: CreateCoachingTaskRequest = {
    ...payload,
    fileIds: [...(payload.fileIds ?? []), ...uploadedFileIds],
    newFiles: undefined,
  };
  return finalPayload;
}

function* onCreateCoachingTask({ payload }: ReturnType<typeof createCoachingTask.request>) {
  try {
    const finalPayload: CreateCoachingTaskRequest = yield call(uploadCoachingTaskFiles, payload);
    const newActivity: Activity = yield call(runCreateCoachingTask, finalPayload);
    yield put(createCoachingTask.success(newActivity));
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (error) {
    yield put(createCoachingTask.failure());
    yield put(handleError({ error }));
  }
}

// Builds one coaching-task activity payload for a single team member. Dropped-in
// files upload to that member's own pool WITHOUT dispatching into the shared
// Files-panel state (uploadCoachingFilesToPool, not the dispatching wrapper) so
// each rep's files don't leak into the manager's own open pool.
function* buildTeamMemberActivityPayload(
  orgId: Organization["id"],
  assigneeId: User["id"],
  payload: CreateCoachingTasksForTeamRequest,
  layoutId: number
) {
  let fileIds = payload.fileIds ?? [];
  if (payload.newFiles && payload.newFiles.length > 0) {
    const { fileIds: uploadedFileIds }: { fileIds: number[] } = yield call(
      uploadCoachingFilesToPool,
      orgId,
      assigneeId,
      payload.newFiles
    );
    fileIds = [...fileIds, ...uploadedFileIds];
  }
  return buildCoachingActivityPayload({
    assigneeId,
    crmActivityTypeId: payload.crmActivityTypeId,
    dueDate: payload.dueDate,
    fileIds,
    layoutId,
    name: payload.name,
    note: payload.note,
    subjectUserId: assigneeId,
  });
}

function* onCreateCoachingTasksForTeam({
  payload,
}: ReturnType<typeof createCoachingTasksForTeam.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.defaultLayout.id;

    // Build per-rep payloads one rep at a time (not a single fanned-out all())
    // so a large team with attachments doesn't burst N-reps × M-files uploads at
    // once. With no dropped-in files this loop makes no API calls, so the common
    // case stays instant and the single bulk-create below does the real work.
    // Trade-off: files upload before the bulk create, so if a later upload or the
    // bulk call fails, earlier reps keep an orphaned pool file with no task —
    // accepted, mirroring the single-create path (upload-then-create).
    const payloads: ActivityPayload[] = [];
    for (const assigneeId of payload.assigneeIds) {
      const memberPayload: ActivityPayload = yield call(
        buildTeamMemberActivityPayload,
        organization.id,
        assigneeId,
        payload,
        layoutId
      );
      payloads.push(memberPayload);
    }

    // Coaching tasks skip layout validation (ignoreLayout) — mandatory
    // activity-layout fields must not block bulk creation.
    const response: Activity[] | Activity[][] = yield callApi(
      "createActivitiesInBulk",
      organization.id,
      payloads,
      { params: { ignoreLayout: true } }
    );
    // The bulk endpoint may return one array per created activity — flatten to
    // a plain Activity[] (the sibling bulk caller flattens the same way).
    const created: Activity[] = response.flat();

    yield put(createCoachingTasksForTeam.success(created));
    yield put(notifyAboutChanges({ added: created, entityType: EntityType.ACTIVITY }));
    // Mentions are in-session only; seed each created task so a chip renders if
    // its rep's tab is ever viewed in this session (mirrors the single path).
    const { mentions } = payload;
    if (mentions && mentions.length > 0) {
      yield all(created.map((task) => put(setCoachingTaskMentions({ mentions, taskId: task.id }))));
    }
    if (payload.onSuccess) {
      yield call(payload.onSuccess, created.length);
    }
  } catch (error) {
    yield put(createCoachingTasksForTeam.failure());
    yield put(handleError({ error }));
  }
}

function* onUpdateCoachingTask({ payload }: ReturnType<typeof updateCoachingTask.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const { activity, dueDate, fileIds, name, newFiles, note } = payload;
    const layoutId = activityLayoutModel.getLayoutFor(activity).id;
    const { files: _files, ...rest } = activity;

    // Atomic upload→update: drop-in files go to the rep's pool first, then
    // their platform fileIds fold into crm_activities.files alongside the
    // picker-linked ids, mirroring the create flow. The pool is keyed by
    // subjectUserId; a coaching task without one can't accept new files —
    // throw so the user sees a failure instead of silently losing them.
    let uploadedFileIds: number[] = [];
    if (newFiles && newFiles.length > 0) {
      if (!isNotEmpty(activity.subjectUserId)) {
        throw new Error("Cannot attach files to a task without a subject user");
      }
      const result: { fileIds: number[] } = yield call(
        uploadAndResolveCoachingFiles,
        organization.id,
        activity.subjectUserId,
        newFiles
      );
      uploadedFileIds = result.fileIds;
    }
    const combinedFileIds =
      fileIds !== undefined || uploadedFileIds.length > 0
        ? [...(fileIds ?? []), ...uploadedFileIds]
        : undefined;

    const nextStartAt =
      dueDate && activity.allDay ? endOfDay(parseApiDate(dueDate)).toISOString() : dueDate;

    const updatePayload: Partial<ActivityPayload> & { id: Activity["id"] } = {
      ...rest,
      ...(name !== undefined ? { name } : {}),
      ...(note !== undefined ? { note } : {}),
      ...(dueDate !== undefined ? { startAt: nextStartAt ?? null } : {}),
      ...(combinedFileIds !== undefined ? { files: combinedFileIds } : {}),
    };

    // Mirror the activity edit flow: file removals are issued as explicit
    // DELETEs against /crm-activities/:aid/files/:fileId. Sending the kept
    // ids via PUT updates the join but doesn't actually delete the dropped
    // files — the BE only removes them when the dedicated DELETE fires.
    if (fileIds !== undefined) {
      const oldFileIds = (activity.files ?? []).map(({ id }) => id);
      const removedFileIds = oldFileIds.filter((id) => !fileIds.includes(id));
      if (removedFileIds.length > 0) {
        yield all(
          removedFileIds.map((fileId) =>
            callApi("deleteEntityFile", organization.id, EntityType.ACTIVITY, activity.id, fileId)
          )
        );
      }
    }

    const response: Activity = yield callApi(
      "updateActivity",
      organization.id,
      layoutId,
      updatePayload,
      // Skip layout validation — coaching tasks only edit a few fields and
      // mandatory activity-layout fields must not block the update.
      { ignoreLayout: true, includeFiles: true }
    );

    yield put(updateCoachingTask.success(response));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [response] }));
    if (payload.mentions !== undefined) {
      yield put(setCoachingTaskMentions({ mentions: payload.mentions, taskId: response.id }));
    }
  } catch (error) {
    yield put(updateCoachingTask.failure(payload.activity.id));
    yield put(handleError({ error }));
  }
}

function* onToggleCoachingTaskComplete({
  payload,
}: ReturnType<typeof toggleCoachingTaskComplete.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload).id;
    const { files: _files, ...activity } = payload;

    const response: Activity = yield callApi(
      "updateActivity",
      organization.id,
      layoutId,
      { ...activity, completed: !activity.completed },
      // Toggling completion must not be blocked by mandatory activity-layout fields.
      { ignoreLayout: true }
    );

    yield put(toggleCoachingTaskComplete.success(response));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [response] }));
  } catch (error) {
    yield put(toggleCoachingTaskComplete.failure(payload.id));
    yield put(handleError({ error }));
  }
}

function* onOpenCoachingTaskEdit({ payload: taskId }: ReturnType<typeof openCoachingTaskEdit>) {
  // The reducer has already set editingTaskId synchronously so the modal mounts immediately.
  const tasks: Activity[] = yield select(getCoachingTasks);
  if (tasks.some((task) => task.id === taskId)) {
    return;
  }

  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const task: Activity = yield callApi("fetchActivity", orgId, taskId, {
      includeAccessStatus: true,
      includeFiles: true,
    });
    yield put(injectCoachingTask(task));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onDeleteCoachingTask({ payload }: ReturnType<typeof deleteCoachingTask.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    yield callApi("deleteActivity", organization.id, payload.id, payload.deleteRecurring);
    yield put(deleteCoachingTask.success({ id: payload.id }));
    yield put(notifyAboutChanges({ deletedIds: [payload.id], entityType: EntityType.ACTIVITY }));
  } catch (error) {
    yield put(deleteCoachingTask.failure(payload.id));
    yield put(handleError({ error }));
  }
}

function* onFetchLinkedTasks({ payload }: ReturnType<typeof fetchLinkedTasks.request>) {
  try {
    // Dedup against the cache here (not in the chip) so the chip's
    // useEffect doesn't have to read `linkedTasksById` and re-fire on
    // every cache mutation.
    const cache: ReturnType<typeof getLinkedCoachingTasksById> = yield select(
      getLinkedCoachingTasksById
    );
    const missingIds = payload.taskIds.filter((id) => !isNotEmpty(cache[id]));
    if (missingIds.length === 0) {
      return;
    }
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const $filters: Record<string, unknown> = {
      id: { $in: missingIds },
      includeAccessStatus: true,
      includeCoachingTasks: true,
    };
    const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters,
      $limit: missingIds.length,
    });
    yield put(fetchLinkedTasks.success(response.data));
  } catch (error) {
    yield put(fetchLinkedTasks.failure());
    yield put(handleError({ error }));
  }
}

function* onFetchSuggestedActionOwner({
  payload,
}: ReturnType<typeof fetchSuggestedActionOwner.request>) {
  if (!isNotEmpty(payload.record)) {
    yield put(fetchSuggestedActionOwner.success(null));
    return;
  }
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { id, entity } = payload.record;
    let record: Company | Deal | Person;
    if (entity === EntityType.COMPANY) {
      record = yield callApi("fetchCompany", orgId, id);
    } else if (entity === EntityType.PERSON) {
      record = yield callApi("fetchPerson", orgId, id);
    } else {
      record = yield callApi("fetchDeal", orgId, id);
    }
    yield put(fetchSuggestedActionOwner.success(record.user ?? null));
  } catch (error) {
    yield put(fetchSuggestedActionOwner.failure());
    yield put(handleError({ error }));
  }
}

export function* coachingTasksSagas() {
  yield takeLatest(fetchCoachingTasks.request, onFetchCoachingTasks);
  yield takeLatest(fetchSuggestedActionOwner.request, onFetchSuggestedActionOwner);
  yield takeEvery(createCoachingTask.request, onCreateCoachingTask);
  yield takeEvery(createCoachingTasksForTeam.request, onCreateCoachingTasksForTeam);
  yield takeEvery(updateCoachingTask.request, onUpdateCoachingTask);
  yield takeEvery(toggleCoachingTaskComplete.request, onToggleCoachingTaskComplete);
  yield takeEvery(deleteCoachingTask.request, onDeleteCoachingTask);
  yield takeLatest(openCoachingTaskEdit, onOpenCoachingTaskEdit);
  yield takeEvery(fetchLinkedTasks.request, onFetchLinkedTasks);
}
